import { v4 as uuid } from 'uuid';
import store from '@Store';
import compact from 'lodash/compact';
import each from 'lodash/each';
import every from 'lodash/every';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import isNaN from 'lodash/isNaN';
import isNull from 'lodash/isNull';
import get from 'lodash/get';
import noop from 'lodash/noop';
import map from 'lodash/map';
import pick from 'lodash/pick';
import reject from 'lodash/reject';
import { getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import Auth from '@Services/Auth';
import RxSocket from '@Services/RxSocket';
import { socketConfig } from '@Constants/CommonConstants';
import { FEATURES } from '@Constants/rx-ui/constants';

import { citations } from '@Constants';
import { concatLowerCase } from '@Utils/String';

import StructureContainers from '@Services/StructureContainers';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import Search from '@Services/Search';
import * as Results from '@Services/Results';
import PageData from '@Services/PageData';
import Catalyst from '@Services/Catalyst';
import * as AdvancedQuery from '@Services/AdvancedQuery';
import UserDatabases from '@Services/UserDatabases';
import NewRelicService, { BROWSER_INTERACTIONS_NAMES } from '@Services/NewRelicService';
import ResultsInfo from '@Services/ResultsInfo';
import ResultsInterface from '@Services/ResultsInterface';
import SearchStorage from '@Services/SearchStorage';
import ResultsPreviewStorage from '@Services/AppDBStorage/Storages/ResultsPreviewStorage';

import { dispatchCreateAlert } from '@Pages/Alerts/AlertsComponents/AlertCreateDialog/AlertCreateDialogActions';
import QuickResultsService from '@Services/QuickResults';
import { updatePreviewItems } from '@Containers/ResultsPreview/actions';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';

import {
  allResults as allResultsSelector,
  previewResults as previewResultsSelector,
  structure as structureSelector,
  query as querySelector,
  isQueryEnabled as isQueryEnabledSelector,
  isResultsPreviewReady as isResultsPreviewReadySelector,
  shouldTrackSearchResults as shouldTrackSearchResultsSelector,
  requestId as requestIdSelector,
  searchId as searchIdSelector,
} from './QuickResultsSelectors';
import {
  quickResultsSetIsLoadingReducerType,
  quickResultsSetAllResultsReducerType,
  quickResultsSetPreviewResultsReducerType,
  quickResultsSetStructureReducerType,
  quickResultsSetStructurePreviewReducerType,
  quickResultsSetQueryReducerType,
  quickResultsSetIsQueryEnabledReducerType,
  quickResultsSetIsBioactivityVisualizationButtonEnabledPreviewReducerType,
  quickResultsSetIsResultsPreviewReadyReducerType,
  quickResultsShouldTrackSearchResultsReducerType,
  quickResultsSetRequestIdReducerType,
  quickResultsSetSearchIdReducerType,
  quickResultsResetStateReducerType,
} from './QuickResultsConstants';

import { structureName as structureNameSelector } from '../BuildQuery/BuildQuerySelectors';

const DEFAULTS = {
  firstItem: 1,
  lastItem: 3,
};

let unsubscribe;
let unsubscribeInfo;
let unsubscribeWSErrors;
let unregisterWS;

export const setUnsubscribe = (handler) => {
  unsubscribe = handler;
};
export const setUnsubscribeInfo = (handler) => {
  unsubscribeInfo = handler;
};
export const setUnsubscribeWSErrors = (handler) => {
  unsubscribeWSErrors = handler;
};
export const setUnregisterWS = (handler) => {
  unregisterWS = handler;
};

/**
 * @name cancelAllQueries
 * @description Cancel all pending search queries
 */
export const cancelAllQueries = () => (dispatch, getState) => {
  const allResults = allResultsSelector(getState());
  each(allResults, (resultItem) => {
    const resultQuery = get(resultItem, 'query', {});
    // If already finished, don't do anything
    if (resultQuery.finished) {
      return;
    }

    // Flag the query search as cancelled and finished
    resultQuery.cancelled = true;
    resultQuery.finished = true;
    resultQuery.restarted = false;

    ResultsInfo.updateInfo({ canceled: true }, resultItem.queryId);
    Search.cancelQuery(resultItem.queryId);
  });
};

export const initializeWS = () => (dispatch, getState) => {
  unregisterWS = RxSocket.onClose(() => {
    const allResults = allResultsSelector(getState());
    const searchId = searchIdSelector(getState());
    const requestId = requestIdSelector(getState());

    const isAllQueriesFinishedOrCanceled = every(allResults, ({ query }) => !query || query.finished || query.canceled);

    if (isAllQueriesFinishedOrCanceled) {
      // Already got results, or canceled
      return;
    }

    if (unsubscribe) {
      unsubscribe();
    }

    if (unsubscribeInfo) {
      unsubscribeInfo();
    }

    if (unsubscribeWSErrors) {
      unsubscribeWSErrors();
    }

    dispatch(cancelAllQueries());
    Search.cancelSearch(searchId, requestId);

    NotifyDialogsService.openModalWindow({
      ...socketConfig.ON_DISCONNECT_MODAL,
      trackingOptions: {
        message: socketConfig.ON_DISCONNECT_MODAL.error,
        component: 'WebSocket',
        prefix: 'se-',
      },
    });
  });
};

export function setIsLoading(isLoading) {
  return {
    type: quickResultsSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setAllResults(allResults) {
  return {
    type: quickResultsSetAllResultsReducerType,
    payload: { allResults },
  };
}

export function setPreviewResults(previewResults) {
  return {
    type: quickResultsSetPreviewResultsReducerType,
    payload: { previewResults },
  };
}

export function setStructure(structure) {
  return {
    type: quickResultsSetStructureReducerType,
    payload: { structure },
  };
}

export function setStructurePreview(structurePreview) {
  return {
    type: quickResultsSetStructurePreviewReducerType,
    payload: { structurePreview },
  };
}

export function setQuery(query) {
  return {
    type: quickResultsSetQueryReducerType,
    payload: { query },
  };
}

export function setIsQueryEnabled(isQueryEnabled) {
  return {
    type: quickResultsSetIsQueryEnabledReducerType,
    payload: { isQueryEnabled },
  };
}

export function setIsBioactivityVisualizationButtonEnabled(isBioactivityVisualizationButtonEnabled) {
  return {
    type: quickResultsSetIsBioactivityVisualizationButtonEnabledPreviewReducerType,
    payload: { isBioactivityVisualizationButtonEnabled },
  };
}

export function setIsResultsPreviewReady(isResultsPreviewReady) {
  return {
    type: quickResultsSetIsResultsPreviewReadyReducerType,
    payload: { isResultsPreviewReady },
  };
}

export function setShouldTrackSearchResults(shouldTrackSearchResults) {
  return {
    type: quickResultsShouldTrackSearchResultsReducerType,
    payload: { shouldTrackSearchResults },
  };
}

export function setRequestId(requestId) {
  return {
    type: quickResultsSetRequestIdReducerType,
    payload: { requestId },
  };
}

export function setSearchId(searchId) {
  return {
    type: quickResultsSetSearchIdReducerType,
    payload: { searchId },
  };
}

export function resetState() {
  return {
    type: quickResultsResetStateReducerType,
  };
}

export function dispatchResetState() {
  store.dispatch(resetState);
}

/**
 * @name isGroupFullyLoaded
 * @param {String, null} groupId - string for filtering by groupId or null for checking all results
 * @param {Array} allResults - optional results list to check
 * @description checks whether all searches in a group has finished
 */
export const isGroupFullyLoaded = (groupId, allResults) => {
  const acceptAllGroupsId = !groupId;
  return allResults
    .filter((result) => acceptAllGroupsId || result.groupId === groupId)
    .every((result) => get(result, 'query.finished', false));
};

export const initResultsInfo = (previewResults) => {
  ResultsInfo.startLoading(map(previewResults, (item) => pick(item, ['databaseId', 'context', 'queryId', 'groupId', 'feedbacks'])));
};

export const storeStructure = (result, searchId) => {
  const structure = find(result.feedbacks, { category: 'Structure' });

  if (isUndefined(structure)) {
    return;
  }

  SearchStorage.extendItem(searchId, {
    structure: structure.structure,
  });
};

export const updateResultsInfo = (previewResults, searchId) => {
  each(previewResults, (result) => {
    storeStructure(result, searchId);

    const patch = {
      resultsCount: result.resultsCount,
      searchId,
      hitSetKey: result.hitSetKey,
      pageNumber: 1,
      resultName: result.resultName,
      description: result.description,
      ...(result.resolvedByCasApi && { resolvedByCasApi: result.resolvedByCasApi }),
      recordCount: result.recordCount, // Patent Family Clustering
      semanticSearchIncluded: result.semanticSearchIncluded, // Reaxys AI Search indication, needed for summary
      semanticResultName: result.semanticResultName, // Reaxys AI Search indication, needed for summary
      ...(result.assistantEligible !== undefined && { assistantEligible: result.assistantEligible }),
      textQuery: result.textQuery || querySelector(store.getState()), // Reaxys AI Search indication, needed for summary
    };
    ResultsInfo.updateInfo(patch, result.queryId);
  });
};

export const getExcludedRCSResults = (allPreviewResults) => {
  const rcsResults = [];
  const previewResults = [];

  each(allPreviewResults, (result) => {
    if (!UserDatabases.isRCSDatabase(result.databaseId)) {
      previewResults.push(result);
      return;
    }

    if (result.resultsCount > 0) {
      rcsResults.push({
        ...result,
        title: result.databaseLabel,
        fullyLoaded: result.fullyLoaded || get(result, 'query.finished', false),
      });
    }
  });

  return { rcsResults, previewResults };
};

export const getAllDefaultDatabasesResults = (previewResults) => {
  const defaultResults = [];

  each(previewResults, (val) => {
    if (UserDatabases.isDefaultDatabase(val.databaseId)) {
      defaultResults.push(val);
    }
  });

  return defaultResults;
};

export const queryHasNoResult = (query) => {
  if (UserDatabases.isDefaultDatabase(query.databaseId)) {
    return query.resultsCount === 0;
  }
  return false;
};

/**
 * @name getHighestQueryResult
 * @methodOf queryHasAlternative
 * @returns {object} The query with highest resultsCount.
 */
export const getHighestQueryResult = (queries) => {
  // If no alternatives, stop execution
  if (queries.length === 0) {
    return false;
  }

  let highestQuery = { resultsCount: null }; // Fake object with NULL property
  each(queries, (query) => {
    if (query.resultsCount > highestQuery.resultsCount) {
      highestQuery = query;
    }
  });

  return !isNull(highestQuery.resultsCount) && highestQuery;
};

export const queryHasAlternative = (query, allQueries) => {
  const alternativeQueries = reject(allQueries, (item) => {
    if (UserDatabases.isDefaultDatabase(item.databaseId)) {
      return true;
    }

    if (isUndefined(item.resultsCount) || item.resultsCount === 0) {
      return true;
    }

    return item.groupId !== query.groupId;
  });

  const alternativeQuery = getHighestQueryResult(alternativeQueries);

  return alternativeQuery || false;
};

export const updateReaxysDbLabels = (rcsResultsLength, previewResults) => {
  const defaultResults = getAllDefaultDatabasesResults(previewResults);

  if (defaultResults.length + rcsResultsLength !== previewResults.length) {
    each(defaultResults, (item) => {
      // eslint-disable-next-line no-param-reassign
      item.databaseLabel = UserDatabases.getDbName(item.databaseId);
    });
  }
};

export const trackSearchResultsFunc = () => (dispatch, getState) => {
  const shouldTrackSearchResults = shouldTrackSearchResultsSelector(getState());

  // Stop this function if track search has already been executed
  if (shouldTrackSearchResults) {
    return;
  }

  const previewResults = previewResultsSelector(getState());
  const query = querySelector(getState());
  const structure = structureSelector(getState());

  let total = 0;
  const resultsByType = map(previewResults, (value) => {
    total += value.resultsCount;
    return {
      name: value.context,
      results: value.resultsCount ? value.resultsCount.toString() : null,
    };
  });

  if (total === undefined || isNaN(total)) {
    return;
  }

  dispatch(setShouldTrackSearchResults(!shouldTrackSearchResults));

  let criteria;

  if (structure && query) {
    criteria = `
      structure(is);
      set &${query}`;
  }

  if (structure && !query) {
    criteria = 'structure(is)set';
  }

  if (!structure) {
    criteria = query;
  }

  Catalyst.trackSearchResults(
    'searchResultsUpdated',
    {
      criteria,
      dataFormCriteria: query,
      resultsPerPage: previewResults.length.toString(),
      resultsByType,
      totalResults: total.toString(),
      type: 'rx:implicit:general search',
    },
    store.getState()
  );

  const currentUser = Auth.getUser();

  const disableTracking = get(currentUser, 'details.disableTracking', {});

  // eslint-disable-next-line no-prototype-builtins
  if (disableTracking.hasOwnProperty('PENDO') && !disableTracking.PENDO && get(window, 'pendo.track')) {
    window.pendo.track('newPage', {
      visitor_id: window.pendo.getVisitorId(),
      account_id: window.pendo.getAccountId(),
      url: `${window.location.origin}/#/preview`,
    });
  }
};

export const reduceDefaultDbResults = (defaultDbResults, previewResults, rcsResults) =>
  // Check no results and insert alternative query if found
  defaultDbResults.reduce((memo, val) => {
    memo.push(val);

    if (!queryHasNoResult(val)) {
      return memo;
    }

    const alternativeQuery = queryHasAlternative(val, previewResults);

    if (alternativeQuery) {
      if (UserDatabases.isDefaultDatabase(val.databaseId)) {
        // If original query is from Reaxys - insert alternative after
        memo.push(alternativeQuery);
      } else {
        // If original query is not from Reaxys - replace original with alternative
        memo.splice(-1, 1, alternativeQuery);
      }
    } else {
      // Hide Reaxys 0 results if there is no alternative from other DB and there is RCS results for the same group
      const queryHasRcsResults = queryHasAlternative(val, rcsResults);
      if (queryHasRcsResults) {
        memo.pop();
      }
    }

    return memo;
  }, []);

export const parseQuickResults = (allPreviewResults, response) => (dispatch, getState) => {
  const requestId = requestIdSelector(getState());
  const searchId = searchIdSelector(getState());
  if (get(response, 'info.requestId') !== requestId) {
    return;
  }

  dispatch(setAllResults(allPreviewResults));

  initResultsInfo(allPreviewResults);
  updateResultsInfo(allPreviewResults, searchId);

  const { rcsResults, previewResults } = getExcludedRCSResults(allPreviewResults);
  let defaultDbResults = getAllDefaultDatabasesResults(previewResults);

  defaultDbResults = reduceDefaultDbResults(defaultDbResults, previewResults, rcsResults);

  // Check fully loaded and add NR browser interaction
  defaultDbResults.forEach((val) => {
    const { groupId, databaseId, resultsCount, context } = val;

    // Fully loaded
    const fullyLoaded = isGroupFullyLoaded(groupId, allPreviewResults);
    if (UserDatabases.isDefaultDatabase(databaseId)) {
      // eslint-disable-next-line no-param-reassign
      val.fullyLoaded = resultsCount > 0 || fullyLoaded;
    } else {
      // eslint-disable-next-line no-param-reassign
      val.fullyLoaded = fullyLoaded;
    }

    // NR browser interaction
    if (fullyLoaded && context === 'substances') {
      NewRelicService.endBrowserInteraction(BROWSER_INTERACTIONS_NAMES.TIME_TO_FIND_SUBSTANCES);
    }

    // Needed for ResultsList
    // eslint-disable-next-line no-param-reassign
    val.isViewPreview = true;
  });

  const updatedPreviewResults = [...defaultDbResults, ...rcsResults];
  dispatch(setPreviewResults(updatedPreviewResults));

  updateReaxysDbLabels(rcsResults.length, updatedPreviewResults);

  dispatch(trackSearchResultsFunc());
  dispatch(updatePreviewItems(updatedPreviewResults, allPreviewResults));
};

export const onInfoProgress = (response) => (dispatch, getState) => {
  const requestId = requestIdSelector(getState());
  if (!response || !response.info || get(response, 'info.requestId') !== requestId) {
    return;
  }

  const allResults = allResultsSelector(getState());
  const queryId = response.info.query || response.payload;
  const resultItem = find(allResults, { queryId });
  const resultQuery = get(resultItem, 'query', {});

  // If no resultItem OR query.finished but restarted is FALSE, stop here
  if (!resultItem || (resultQuery && resultQuery.finished && resultQuery.restarted === false)) {
    return;
  }

  // If we receive the first notification of Search Query started,
  // let's create the object for this query taking the queryId from payload property.
  if (response.message === 'Search started') {
    resultItem.query = {
      finished: false,
      percentage: 1,
    };
  } else if (response.message === 'Search finished') {
    resultItem.query = {
      finished: true,
      percentage: 100,
    };
  } else if (!isUndefined(response.payload.percentage)) {
    if (resultQuery) {
      resultItem.query.percentage = response.payload.percentage;
    } else {
      resultItem.query = {
        finished: false,
        percentage: response.payload.percentage,
      };
    }
  }

  // Mark search results as ready when loading finished
  // Otherwise, socket reconnect will start search again and produce a bug (RXFE-7085)
  if (isGroupFullyLoaded(null, allResults)) {
    dispatch(setIsResultsPreviewReady(true));
  }
};

export const cancelQuery = (queryId, isError) => (dispatch, getState) => {
  if (!queryId) {
    return;
  }

  const allResults = allResultsSelector(getState());
  const previewResults = previewResultsSelector(getState());

  const resultItem = find(allResults, { queryId });

  if (resultItem) {
    // Flag the query search as cancelled and finished
    resultItem.query = {
      cancelled: true,
      finished: true,
      restarted: false,
      error: !!isError,
    };
  }

  // React dispatch - canceled query
  dispatch(updatePreviewItems(previewResults, allResults));

  ResultsInfo.updateInfo({ canceled: true }, queryId);

  // eslint-disable-next-line consistent-return
  return Search.cancelQuery(queryId);
};

export const onInfoError = (error) => (dispatch) => {
  if (!error.queryId) {
    return;
  }
  dispatch(cancelQuery(error.queryId, 'isError'));
};

export const loadEvents = () => (dispatch) => {
  const unsubscribeHandler = QuickResultsService.setupWebSockets((allPreviewResults, response) => {
    // eslint-disable-next-line no-use-before-define
    dispatch(initSpinner());
    dispatch(parseQuickResults(allPreviewResults, response));
  });
  const unsubscribeInfoHandler = QuickResultsService.setupProcessInfoChannel((response) => dispatch(onInfoProgress(response)));
  const unsubscribeWSErrorsHandler = QuickResultsService.subscribeWSErrors((error) => dispatch(onInfoError(error)));

  setUnsubscribe(unsubscribeHandler);
  setUnsubscribeInfo(unsubscribeInfoHandler);
  setUnsubscribeWSErrors(unsubscribeWSErrorsHandler);
};

export const saveInAppStorage = () => (dispatch, getState) => {
  const allResults = allResultsSelector(getState());
  const previewResults = previewResultsSelector(getState());
  const searchId = searchIdSelector(getState());

  if (!previewResults || !allResults) {
    return Promise.reject();
  }

  return ResultsPreviewStorage.set(searchId, {
    previewResults,
    allResults,
  });
};

/**
 * @name cancelSubscriptions
 * @description Destroy events loaded previously
 */
export const cancelSubscriptions = () => (dispatch, getState) => {
  const searchId = searchIdSelector(getState());
  const requestId = requestIdSelector(getState());

  dispatch(cancelAllQueries());

  if (unsubscribe) {
    unsubscribe();
  }

  if (unsubscribeInfo) {
    unsubscribeInfo();
  }

  if (unregisterWS) {
    unregisterWS();
  }

  if (unsubscribeWSErrors) {
    unsubscribeWSErrors();
  }

  dispatch(saveInAppStorage());

  Search.cancelSearch(searchId, requestId);
};

export const initSpinner = () => (dispatch, getState) => {
  const previewResults = previewResultsSelector(getState());
  const isQueryEnabled = isQueryEnabledSelector(getState());

  const value = (previewResults ? previewResults.length === 0 : true) && isQueryEnabled;
  dispatch(setIsLoading(value));
};

/**
 * @name trackQuickResultsNewPage
 * @description track "newPage" event for quick results, if returned from another tabs
 */
export const trackQuickResultsNewPage = () => {
  const trackingPathName = getTrackingPathName(store.getState()) || '';
  Catalyst.trackPage(trackingPathName);

  const triggerView = get(window, 'adobe.target.triggerView', noop);
  triggerView(PageData.getCurrentPageName());
};

export const initSearch = (searchItemFromStorage) => async (dispatch, getState) => {
  const isResultsPreviewReady = isResultsPreviewReadySelector(getState());
  if (isResultsPreviewReady) {
    dispatch(initSpinner());
    return;
  }

  const isQueryEnabled = isQueryEnabledSelector(getState());
  const structure = structureSelector(getState());
  const query = querySelector(getState());
  const searchId = searchIdSelector(getState());

  if (!isQueryEnabled) {
    dispatch(initSpinner());
    return;
  }

  await ResultsPreviewStorage.get(searchId)
    .then(({ previewResults, allResults }) => {
      const isGroupsLoaded = isGroupFullyLoaded(null, allResults);
      if (previewResults.length && allResults.length && isGroupsLoaded) {
        initResultsInfo(allResults);
        updateResultsInfo(allResults, searchId);
        dispatch(setAllResults(allResults));
        dispatch(setPreviewResults(previewResults));
        trackQuickResultsNewPage();

        // React dispatch - stored results
        dispatch(updatePreviewItems(previewResults, allResults));
      } else {
        const requestId = uuid();
        dispatch(setRequestId(requestId));
        Search.searchByQuery(query, searchItemFromStorage.Typeaheads, structure, searchId, requestId, searchItemFromStorage.options);
      }
    })
    .catch(Logger.error);
  dispatch(initSpinner());
};

export const initialize =
  ({ searchId }) =>
  async (dispatch) => {
    dispatch(initializeWS());
    dispatch(loadEvents());
    dispatch(initSpinner());

    // The results route normally receives searchId through location.state. A
    // directly opened /search/quick/results URL has no such state, so there
    // is no query to restore or search request to start.
    if (!searchId) {
      dispatch(setIsQueryEnabled(false));
      dispatch(setIsLoading(false));
      return;
    }

    const searchItemFromStorage = await SearchStorage.get(searchId);
    dispatch(setSearchId(searchId));
    dispatch(setIsQueryEnabled(!!(searchItemFromStorage.query || searchItemFromStorage.structure)));
    dispatch(setQuery(searchItemFromStorage.query));
    dispatch(setStructure(searchItemFromStorage.structureUsed ? searchItemFromStorage.structure : null));
    dispatch(setStructurePreview(searchItemFromStorage.structureUsed ? searchItemFromStorage.structurePreview : null));
    dispatch(
      setIsBioactivityVisualizationButtonEnabled(Auth.isFeatureEnabled(FEATURES.enableBioactivityVisualizationButtonOnPreviewResults))
    );
    dispatch(initSearch(searchItemFromStorage));
  };

/**
 * @name generatePreviewTrackingSections
 * @param {String} feature
 * @param {String} [contextTitle]
 * @returns {Array}
 * @description generates sections array used for analytics tracking
 */
export const generatePreviewTrackingSections = (feature, contextTitle = '') =>
  compact([Catalyst.getPrefix(), 'query', 'preview', concatLowerCase(contextTitle), feature]);

/**
 * @name trackPreviewFeature
 * @methodOf App.controller:QuickResultsController
 * @param {String} feature
 * @param {Object} resultItem
 * @description RXFE-5112 - track preview results feature
 */
export const trackPreviewFeature = (feature, resultItem) => {
  const sections = generatePreviewTrackingSections(feature, get(resultItem, 'title'));
  Catalyst.trackProductFeature(sections.join(':'));
};

/**
 * @name redirectToResults
 * @methodOf App.controller:QuickResultsController
 * @param {Object} requestParams Object containing all info for result set
 * @param {Number} buttonIndex View Results button index for tracking
 * @description Forwarding to ResultsDataManage.redirectToResults
 */
export const redirectToResults = (requestParams, buttonIndex) => async (dispatch, getState) => {
  if (requestParams.context === 'substances') {
    NewRelicService.createBrowserInteraction(BROWSER_INTERACTIONS_NAMES.TIME_TO_VIEW_SUBSTANCES, requestParams);
  }

  const searchId = searchIdSelector(getState());

  Results.logEvent('viewResults', {
    resultName: requestParams.resultName,
    databaseId: requestParams.databaseId,
    context: requestParams.context,
    hitSetKey: requestParams.hitSetKey,
  });

  if (!isUndefined(buttonIndex)) {
    Catalyst.trackSearchResultsClick((+buttonIndex + 1).toString());
  }

  const feedbackWithStructure = find(requestParams.feedbacks, 'structure');

  try {
    await SearchStorage.extendItem(searchId, { [requestParams.context]: feedbackWithStructure });
    await dispatch(saveInAppStorage());
    await ResultsInfo.commitResultsToBreadcrumbs(requestParams.groupId);
    await ResultsDataManage.redirectToResults({
      ...requestParams,
      searchId, // Add searchId to params
      resetBreadcrumbs: false,
    });
  } catch (error) {
    Logger.error(error);
  }
};

export const updatePreviewResults = (resultItem, previewResults) =>
  previewResults.map((result) => {
    if (result.hitSetKey !== resultItem.hitSetKey) {
      return result;
    }
    return resultItem;
  });

export const showMoreResults = (resultItem) => async (dispatch, getState) => {
  const previewResults = previewResultsSelector(getState());
  // eslint-disable-next-line no-param-reassign
  resultItem.isCollapsed = !resultItem.isCollapsed;

  const updatedPreviewResults = updatePreviewResults(resultItem, previewResults);
  dispatch(setPreviewResults(updatedPreviewResults));

  if (resultItem.isCollapsed) {
    return;
  }

  trackPreviewFeature('previewresults', resultItem);

  const {
    databaseId,
    hitSetKey,
    resultName,
    context,
    resultsPreview,
    semanticSearchIncluded,
    semanticResultName,
    assistantEligible,
    textQuery,
  } = resultItem;

  if (resultsPreview) {
    return;
  }

  const handleResults = (items) => {
    // eslint-disable-next-line no-param-reassign
    resultItem.resultsPreview = items;
    // eslint-disable-next-line no-shadow
    const updatedPreviewResults = updatePreviewResults(resultItem, previewResults);
    dispatch(setPreviewResults(updatedPreviewResults));
  };
  ResultsInterface.getResults({
    resultName,
    databaseId,
    hitSetKey,
    context,
    firstItem: DEFAULTS.firstItem,
    lastItem: DEFAULTS.lastItem,
    semanticSearchIncluded,
    semanticResultName,
    assistantEligible,
    textQuery: textQuery || querySelector(store.getState()),
  }).subscribe({ next: ({ data }) => handleResults(data), error: Logger.error });
};

export const cancelQueriesInGroup = (groupId) => (dispatch, getState) => {
  const allResults = allResultsSelector(getState());

  allResults
    .filter((result) => result.groupId === groupId)
    .map((result) => result.queryId)
    .forEach((queryId) => dispatch(cancelQuery(queryId)));
};

export const copyQuery = (query) => async () => {
  trackPreviewFeature('edit in query builder', query);
  try {
    const queryWithSemantic = query.semanticSearchIncluded ? { ...query, semanticQuery: querySelector(store.getState()) } : query;
    const whereClauseImport = await AdvancedQuery.getQueryByWhereClause(queryWithSemantic);

    whereClauseImport.context = query.context;
    if (query.resolvedByCasApi) {
      whereClauseImport.resolvedByCasApi = query.resolvedByCasApi;
    }
    if (query.semanticSearchIncluded) {
      whereClauseImport.isSemanticSearchIncluded = true;
    }
    // RXFE-2359: need to restore user selected options after similar search
    StructureContainers.updateStructureWithDisabledOptions(whereClauseImport, 'qs-container');
  } catch (error) {
    Logger.error(error);
  }
};

export const createAlert = (resultItem) => {
  const alert = {
    quickSearchItem: {
      whereClauseImportToken: resultItem.whereClauseImportToken,
    },
    context: resultItem.context,
    description: resultItem.description,
    databaseId: resultItem.databaseId,
    trackingType: generatePreviewTrackingSections(null, resultItem.context).join(':'),
  };

  trackPreviewFeature('createalert', resultItem);
  dispatchCreateAlert(alert);
};

export const getEditInQueryBuilderButtonAttrs = ({ context, whereClauseImportToken } = {}) =>
  context === citations &&
  whereClauseImportToken &&
  whereClauseImportToken.indexOf('CBI.BICIT') > -1 &&
  Auth.isFeatureEnabled(FEATURES.enableEditSynonyms)
    ? {
        label: 'Edit Synonyms',
        e2eName: 'e2e-edit-qb e2e-edit-synonyms',
      }
    : {
        label: 'Edit in Query Builder',
        e2eName: 'e2e-edit-qb',
      };

export const goToNewSearch = () => (dispatch) => {
  trackPreviewFeature('new');
  history.push({
    pathname: paths.SEARCH,
    state: { action: 'new' },
  });
  dispatch(resetState());
};

export const goToEditSearch = () => (dispatch) => {
  trackPreviewFeature('edit query');
  history.push({
    pathname: paths.SEARCH,
    state: { action: 'edit' },
  });
  dispatch(resetState());
};

export const showStructurePopup = (structurePreview) => (dispatch, getState) => {
  const structureName = structureNameSelector(getState());

  dispatch(openStructureDialog(uuid(), structurePreview, structureName));
};
